import { useState } from "react";
import { Button } from "@/components/ui/button";
import { RecordPanel } from "./RecordFrame";

/**
 * A titled block of free text with Edit (Add when empty); saves through the page's own field writer.
 * A failed save keeps the editor open with the draft intact. The writer's own error handler toasts.
 */
export function EditableTextPanel({ title, value, onSave, emptyText, id }: {
  title: string; value: string | null | undefined; onSave: (v: string) => Promise<unknown>; emptyText: string; id?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <RecordPanel
      title={title}
      action={!editing && (
        <button type="button" onClick={() => { setDraft(value ?? ""); setEditing(true); }} className="text-xs font-semibold text-primary-text hover:underline">
          {value ? "Edit" : "Add"}
        </button>
      )}
    >
      {editing ? (
        <div className="space-y-2">
          <textarea id={id} value={draft} onChange={(e) => setDraft(e.target.value)} className="min-h-[90px] w-full rounded-md border bg-background p-2 text-[13px]" aria-label={title} />
          <div className="flex justify-end gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <Button size="sm" disabled={saving} onClick={async () => {
              setSaving(true);
              try { await onSave(draft); setEditing(false); }
              catch { /* the caller's save hook already told the user; stay open so they can retry */ }
              finally { setSaving(false); }
            }}>Save</Button>
          </div>
        </div>
      ) : value ? (
        <p className="whitespace-pre-line text-[13px] text-muted-foreground">{value}</p>
      ) : (
        <p className="text-[12.5px] text-muted-foreground">{emptyText}</p>
      )}
    </RecordPanel>
  );
}
